import { yupResolver } from '@hookform/resolvers/yup';
import {
  Box,
  FormControlLabel, Grid, RadioGroup, Typography,
} from '@mui/material';
import { selectAuthUser } from 'base/auth/selectors';
import {
  ClosableModal,
  SecondaryButton, ErrorMessage, FileDragUploader, PrimaryButton, PrimaryRadio,
  PrimaryTextField,
  PrimaryLoadingButton,
} from 'components';
import { UploadResponse, UploadScenarios } from 'interfaces/file-uploader';
import { INewsfeedModalForm } from 'pages/newsfeed/interface';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useConfirmation } from 'base/common';
import { selectNewsfeedsLoader } from '../newsfeed-list/selector';
import { newsfeedAction } from '../newsfeed-list/slice';
import uploadNewsfeedValidationSchema from './validation/add-newsfeed';

const AddNewsfeedModal = () => {
  const [showNewsFeedModal, setShowNewsFeedModal] = useState(false);

  const loading = useSelector(selectNewsfeedsLoader);

  const authUser = useSelector(selectAuthUser);
  const dispatch = useDispatch();
  const confirm = useConfirmation();

  const {
    register, handleSubmit, formState: { errors }, control,
    setValue, setError, clearErrors, reset, watch,
  } = useForm<INewsfeedModalForm>({
    defaultValues: {
      caption: '',
      mediaUrl: '',
      postType: 'default',
      visibility: authUser?.attributes?.accountType === 'subscription' ? 'subscriber-only' : 'public',
    },

    resolver: yupResolver(uploadNewsfeedValidationSchema),
  });

  const opennewsfeedModal = () => {
    if (authUser?.attributes?.isActive) {
      setShowNewsFeedModal(true);
    } else {
      confirm({
        variant: 'info',
        icon: 'warning',
        message: 'Your expert account has not been activated. Please contact your admin',
      });
    }
  };

  const closeNewsfeedModal = () => {
    reset();
    setShowNewsFeedModal(false);
  };

  const handleUpload = (res: UploadResponse) => {
    clearErrors('mediaUrl');
    setValue('fileType', res.extension.toUpperCase());
    setValue('fileCategory', res.category);
    setValue('name', res.name);
    if (res.statusCode !== 201) {
      setError('mediaUrl', { type: 'custom', message: res.message });
      setValue('mediaUrl', '');
    } else {
      setValue('mediaUrl', res.url);
    }
  };

  const fileUrl = watch('mediaUrl');
  const fileType = watch('fileType', '');

  const handleVisibleTypeChange = (event: any) => {
    setValue('visibility', event.target.value);
  };

  const onSubmit: SubmitHandler<INewsfeedModalForm> = React.useCallback(async (data) => {
    await new Promise((resolve, reject) => {
      dispatch(newsfeedAction.createNewsfeed({ ...data, resolve, reject }));
    });
    closeNewsfeedModal();
  }, [dispatch]);

  return (
    <>
      <PrimaryButton size="large" fullWidth onClick={opennewsfeedModal}>
        Create new post
      </PrimaryButton>
      <ClosableModal
        open={showNewsFeedModal}
        onClose={closeNewsfeedModal}
        title="Create new post"
        subTitle="Create post to share among the community"
        modalClass="media-gallery-modal"
      >
        <form data-testid="newsfeed-modal-form">
          <div className="form-item">
            <Controller
              control={control}
              name="mediaUrl"
              render={() => (
                <FileDragUploader
                  textLabel="Upload file"
                  handleFile={handleUpload}
                  scenario={UploadScenarios.sessionHighlightMedia}
                  currentContentUrl={fileUrl}
                  uploadAnotherText="Upload another media"
                  currentMediaType={fileType ?? ''}
                />
              )}
            />
            <div className="mb-3 mt-1">
              <ErrorMessage>{errors?.mediaUrl?.message}</ErrorMessage>
            </div>
          </div>
          <div className="mb-3 mt-1">
            <Box display="flex" gap={2} />
          </div>
          <div className="form-item">
            <Controller
              control={control}
              name="caption"
              render={() => (
                <PrimaryTextField sx={{ height: 145 }} multiline rows={3} placeholder="Caption here" {...register('caption')} />
              )}
            />
            <div className="mb-3 mt-1">
              <ErrorMessage>{errors?.caption?.message}</ErrorMessage>
            </div>
          </div>
          {authUser.attributes.accountType !== 'public'
            ? (
              <div className="form-item">
                <Controller
                  control={control}
                  name="visibility"
                  render={({ field }) => (
                    <RadioGroup {...field} onChange={handleVisibleTypeChange}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>Make this post visible for</Typography>
                      <Grid container justifyContent="start-flex">
                        <Grid item>
                          <FormControlLabel value="public" control={<PrimaryRadio aria-label="public-radio" />} label="Public" />
                        </Grid>
                        <Grid item>
                          <FormControlLabel value="subscriber-only" control={<PrimaryRadio aria-label="subscriber-only-radio" />} label="Subscribers only" />
                        </Grid>
                      </Grid>

                    </RadioGroup>
                  )}
                />
                <div className="mb-3 mt-1">
                  <ErrorMessage>{errors?.visibility?.message}</ErrorMessage>
                </div>
              </div>
            )
            : ''}
          <div className="row">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <SecondaryButton fullWidth onClick={closeNewsfeedModal}>
                Cancel
              </SecondaryButton>
            </div>
            <div className="col-12 col-md-6">
              <PrimaryLoadingButton
                loading={loading}
                fullWidth
                type="submit"
                onClick={handleSubmit(onSubmit)}
              >
                Post now
              </PrimaryLoadingButton>
            </div>
          </div>
        </form>
      </ClosableModal>
    </>
  );
};
export default AddNewsfeedModal;
